import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import Icon from '../../components/AppIcon';
import UserCrudTable from './components/UserCrudTable';
import UserFormModal from './components/UserFormModal';
import UserViewModal from './components/UserViewModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const UsersCrudManagement = () => {
  const [language, setLanguage] = useState('en');
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formMode, setFormMode] = useState('add');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);

    // Mock data
    setUsers([
      { id: 1, name: 'Ahmed Hassan', phoneNumber: '+20 123 456 7890', userType: 'Client' },
      { id: 2, name: 'Sarah Johnson', phoneNumber: '+1 555 123 4567', userType: 'Workshop' },
      { id: 3, name: 'Mohammed Ali', phoneNumber: '+971 50 123 4567', userType: 'Agency' },
      { id: 4, name: 'John Smith', phoneNumber: '+44 20 7123 4567', userType: 'Emergency Provider' },
      { id: 5, name: 'Fatima Zahra', phoneNumber: '+212 6 12 34 56 78', userType: 'Client' }
    ]);
  }, []);

  const labels = {
    title: { en: 'Users Management', ar: 'إدارة المستخدمين' },
    addNew: { en: 'Add New User', ar: 'إضافة مستخدم جديد' },
    search: { en: 'Search users...', ar: 'البحث عن المستخدمين...' },
    noResults: { en: 'No users found', ar: 'لم يتم العثور على مستخدمين' }
  };

  const handleAddUser = () => {
    setFormMode('add');
    setSelectedUser(null);
    setIsFormModalOpen(true);
  };

  const handleEditUser = (user) => {
    setFormMode('edit');
    setSelectedUser(user);
    setIsFormModalOpen(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleSaveUser = (userData) => {
    if (formMode === 'add') {
      const newUser = { ...userData, id: Date.now() };
      setUsers([...users, newUser]);
    } else {
      setUsers(users?.map(u => u?.id === selectedUser?.id ? { ...u, ...userData } : u));
    }
    setIsFormModalOpen(false);
  };

  const handleConfirmDelete = () => {
    setUsers(users?.filter(u => u?.id !== selectedUser?.id));
    setIsDeleteModalOpen(false);
  };

  const filteredUsers = users?.filter(user =>
    user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    user?.phoneNumber?.includes(searchQuery) ||
    user?.userType?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {labels?.title?.[language]}
          </h1>
        </div>

        <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={labels?.search?.[language]}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full"
              />
            </div>
            <Button
              onClick={handleAddUser}
              iconName="Plus"
              variant="default"
            >
              {labels?.addNew?.[language]}
            </Button>
          </div>

          {filteredUsers?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-text-secondary">{labels?.noResults?.[language]}</p>
            </div>
          ) : (
            <UserCrudTable
              users={filteredUsers}
              language={language}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onView={handleViewUser}
            />
          )}
        </div>
      </div>
      {isFormModalOpen && (
        <UserFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={handleSaveUser}
          user={selectedUser}
          mode={formMode}
          language={language}
        />
      )}
      {isViewModalOpen && (
        <UserViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          user={selectedUser}
          language={language}
          onEdit={() => {
            setIsViewModalOpen(false);
            handleEditUser(selectedUser);
          }}
          onDelete={() => {
            setIsViewModalOpen(false);
            handleDeleteUser(selectedUser);
          }}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemName={selectedUser?.name}
          language={language}
        />
      )}
    </div>
  );
};

export default UsersCrudManagement;